import { Controller } from "@hotwired/stimulus"
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend } from "chart.js"

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip, Legend)

export default class extends Controller {
  static targets = ["canvas", "resetBtn"]
  static values = {
    labels: Array,
    datasets: Object,
    statusConfig: Object
  }

  connect() {
    const self = this
    const datasets = []
    const dsValues = this.datasetsValue
    const statusConfig = this.statusConfigValue

    for (const [key, config] of Object.entries(statusConfig)) {
      const data = dsValues[key]
      if (!data || data.every(v => v === 0)) continue

      datasets.push({
        label: config.label,
        data: data,
        borderColor: config.color,
        backgroundColor: config.color + "18",
        tension: 0.4,
        fill: false,
        pointBackgroundColor: config.color,
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        borderWidth: 2.5
      })
    }

    this.chart = new Chart(this.canvasTarget, {
      type: "line",
      data: {
        labels: this.labelsValue,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index"
        },
        plugins: {
          legend: {
            position: "top",
            align: "end",
            labels: {
              padding: 16,
              usePointStyle: true,
              pointStyle: "circle",
              font: { size: 11 }
            },
            onClick: (e, legendItem, legend) => {
              const ci = legend.chart
              const index = legendItem.datasetIndex
              const allVisible = ci.data.datasets.every((_, i) => ci.isDatasetVisible(i))
              const onlyThisVisible = ci.data.datasets.every((_, i) =>
                i === index ? ci.isDatasetVisible(i) : !ci.isDatasetVisible(i)
              )

              if (onlyThisVisible) {
                ci.data.datasets.forEach((_, i) => ci.setDatasetVisibility(i, true))
              } else if (allVisible) {
                ci.data.datasets.forEach((_, i) => ci.setDatasetVisibility(i, i === index))
              } else {
                ci.setDatasetVisibility(index, !ci.isDatasetVisible(index))
              }
              ci.update()
              self.updateResetButton()
            }
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12,
            titleFont: { size: 13 },
            bodyFont: { size: 12 }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 11 },
              color: "rgba(0, 0, 0, 0.4)",
              maxRotation: 45
            }
          },
          y: {
            beginAtZero: true,
            stacked: false,
            grid: { color: "rgba(0, 0, 0, 0.05)" },
            ticks: {
              precision: 0,
              font: { size: 11 },
              color: "rgba(0, 0, 0, 0.4)"
            }
          }
        }
      }
    })
  }

  showAll() {
    if (!this.chart) return
    this.chart.data.datasets.forEach((_, i) => this.chart.setDatasetVisibility(i, true))
    this.chart.update()
    this.updateResetButton()
  }

  updateResetButton() {
    if (!this.hasResetBtnTarget || !this.chart) return
    const allVisible = this.chart.data.datasets.every((_, i) => this.chart.isDatasetVisible(i))
    this.resetBtnTarget.classList.toggle("hidden", allVisible)
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }
}
